/**
 * Editable form for `AutoCampaignConfigOverride`. Used inside the enrollment
 * accordion (Screen 1) AND the standalone Config dialog (Screen 6). Owns its
 * local draft state so parent components can decide when/how to persist.
 *
 * Field validation ranges mirror `AutoCampaignConfigOverrideSchema` in
 * packages/types/src/auto-campaign.ts. We don't wire zod here because the API
 * rejects out-of-range values with a clear field_errors payload — no need for
 * client-side duplication of the same rules.
 */
import { useEffect, useState } from 'react';
import type {
  AutoCampaignConfigOverride,
  AutoCampaignOffpagePlatformCode,
} from '@seo/types';
import { AUTO_CAMPAIGN_API_DEFAULTS } from '@seo/types';
import { FormField, TextField } from '@/components/ui/form-field';
import { cn } from '@/lib/cn';

const PLATFORMS: {
  code: AutoCampaignOffpagePlatformCode;
  label: string;
}[] = [
  { code: 'linkedin', label: 'LinkedIn' },
  { code: 'quora', label: 'Quora' },
  { code: 'reddit', label: 'Reddit' },
  { code: 'medium', label: 'Medium' },
];

const TIMEZONES = [
  'Asia/Kolkata',
  'America/Los_Angeles',
  'America/New_York',
  'Europe/London',
  'Europe/Berlin',
  'Australia/Sydney',
  'UTC',
];

export interface ConfigFormValues {
  briefs_per_fire: number;
  variants_per_platform: number;
  offpage_platforms: AutoCampaignOffpagePlatformCode[];
  timezone: string;
  schedule_exec_cron: string;
  schedule_delivery_cron: string;
  force_full_run: boolean;
  pdf_attach_max: number;
}

const DEFAULTS: ConfigFormValues = {
  briefs_per_fire: AUTO_CAMPAIGN_API_DEFAULTS.briefs_per_fire,
  variants_per_platform: AUTO_CAMPAIGN_API_DEFAULTS.variants_per_platform,
  offpage_platforms: Array.from(AUTO_CAMPAIGN_API_DEFAULTS.offpage_platforms),
  timezone: AUTO_CAMPAIGN_API_DEFAULTS.timezone,
  schedule_exec_cron: AUTO_CAMPAIGN_API_DEFAULTS.schedule_exec_cron,
  schedule_delivery_cron: AUTO_CAMPAIGN_API_DEFAULTS.schedule_delivery_cron,
  force_full_run: false,
  pdf_attach_max: AUTO_CAMPAIGN_API_DEFAULTS.pdf_attach_max,
};

export interface ConfigFormProps {
  /** Initial values — typically the current effective config from the server. */
  initial?: Partial<ConfigFormValues>;
  /** Fires on every field change; parent decides whether to buffer or auto-save. */
  onChange?: (values: ConfigFormValues) => void;
  /** Show the "Force full run" toggle (only makes sense on the Config dialog). */
  showForceFullRun?: boolean;
  /** Show the schedule cron fields (only in the Config dialog, not enrollment). */
  showSchedule?: boolean;
  className?: string;
}

export function ConfigForm({
  initial,
  onChange,
  showForceFullRun = false,
  showSchedule = false,
  className,
}: ConfigFormProps): JSX.Element {
  const [values, setValues] = useState<ConfigFormValues>({
    ...DEFAULTS,
    ...initial,
  });

  useEffect(() => {
    onChange?.(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  function togglePlatform(code: AutoCampaignOffpagePlatformCode): void {
    setValues((v) => {
      const has = v.offpage_platforms.includes(code);
      return {
        ...v,
        offpage_platforms: has
          ? v.offpage_platforms.filter((c) => c !== code)
          : [...v.offpage_platforms, code],
      };
    });
  }

  return (
    <div className={cn('space-y-4', className)}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <TextField
          label="Briefs per fire"
          type="number"
          min={1}
          max={15}
          value={values.briefs_per_fire}
          onChange={(e) =>
            setValues((v) => ({
              ...v,
              briefs_per_fire: clampInt(e.target.value, 1, 15, v.briefs_per_fire),
            }))
          }
          hint="How many content briefs each auto-fire generates (1-15)."
        />
        <TextField
          label="Variants per platform"
          type="number"
          min={1}
          max={5}
          value={values.variants_per_platform}
          onChange={(e) =>
            setValues((v) => ({
              ...v,
              variants_per_platform: clampInt(
                e.target.value,
                1,
                5,
                v.variants_per_platform,
              ),
            }))
          }
          hint="Off-page variants generated per platform per brief (1-5)."
        />
        <TextField
          label="Max PDFs per digest"
          type="number"
          min={0}
          max={30}
          value={values.pdf_attach_max}
          onChange={(e) =>
            setValues((v) => ({
              ...v,
              pdf_attach_max: clampInt(e.target.value, 0, 30, v.pdf_attach_max),
            }))
          }
          hint="How many PDF documents each Telegram digest attaches (0-30). A full fire produces ~22."
        />
      </div>

      <FormField label="Off-page platforms">
        <div className="flex flex-wrap gap-2">
          {PLATFORMS.map((p) => {
            const checked = values.offpage_platforms.includes(p.code);
            return (
              <label
                key={p.code}
                className={cn(
                  'inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors',
                  checked
                    ? 'border-primary/40 bg-primary/10 text-primary'
                    : 'border-input bg-background text-muted-foreground hover:bg-accent',
                )}
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-primary"
                  checked={checked}
                  onChange={() => togglePlatform(p.code)}
                />
                {p.label}
              </label>
            );
          })}
        </div>
      </FormField>

      {showSchedule ? (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="Timezone">
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={values.timezone}
                onChange={(e) =>
                  setValues((v) => ({ ...v, timezone: e.target.value }))
                }
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </FormField>
            <TextField
              label="Execution cron"
              value={values.schedule_exec_cron}
              onChange={(e) =>
                setValues((v) => ({
                  ...v,
                  schedule_exec_cron: e.target.value,
                }))
              }
              hint="5 fields, tz above. Default: Sun/Wed 22:00."
            />
          </div>
          <TextField
            label="Delivery cron"
            value={values.schedule_delivery_cron}
            onChange={(e) =>
              setValues((v) => ({
                ...v,
                schedule_delivery_cron: e.target.value,
              }))
            }
            hint="When the Telegram digest is sent. Default: Mon/Thu 11:00."
          />
        </>
      ) : null}

      {showForceFullRun ? (
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-primary"
            checked={values.force_full_run}
            onChange={(e) =>
              setValues((v) => ({ ...v, force_full_run: e.target.checked }))
            }
          />
          <span>
            <span className="font-medium">Force full run on next fire</span>
            <span className="block text-xs text-muted-foreground">
              Bypasses the 15-day skip for A1 audit and A2 keyword refresh — the
              next auto-fire will run the entire DAG regardless of staleness.
            </span>
          </span>
        </label>
      ) : null}
    </div>
  );
}

/**
 * Turn config form values into a `AutoCampaignConfigOverride` — only
 * platforms with at least one selection are sent; blanks fall back to defaults
 * on the server.
 */
export function toOverridePayload(
  values: ConfigFormValues,
  showSchedule: boolean,
  showForceFullRun: boolean,
): AutoCampaignConfigOverride {
  const payload: AutoCampaignConfigOverride = {
    briefs_per_fire: values.briefs_per_fire,
    variants_per_platform: values.variants_per_platform,
    offpage_platforms: values.offpage_platforms,
    pdf_attach_max: values.pdf_attach_max,
  };
  if (showSchedule) {
    payload.timezone = values.timezone;
    payload.schedule_exec_cron = values.schedule_exec_cron;
    payload.schedule_delivery_cron = values.schedule_delivery_cron;
  }
  if (showForceFullRun) {
    payload.force_full_run = values.force_full_run;
  }
  return payload;
}

function clampInt(raw: string, min: number, max: number, fallback: number): number {
  const n = Number.parseInt(raw, 10);
  if (Number.isNaN(n)) return fallback;
  if (n < min) return min;
  if (n > max) return max;
  return n;
}

export { DEFAULTS as CONFIG_DEFAULTS };
